import {
  currentAuthenticatedUser,
  handleSignIn,
  handleConfirmSignIn,
  handleResetPassword,
  handleConfirmResetPassword,
  handleSignOut,
} from '@/api/users'
import { SessionUtils } from '@azimut-mfe/styleguide'

function extractPermissions(auth) {
  return getRestPermissionsfromToken(auth.idToken.payload.pms).concat(
    getGraphQLPermissionsfromToken(auth.idToken.payload.pmsgraphql)
  )
}

function getRestPermissionsfromToken(permissionsObject) {
  return permissionsObject
    .split(',')
    .map((permission) => {
      const [
        resource,
        allowParameters,
        denyParameters,
        allowFields,
        denyFields,
      ] = permission.split(':')

      return {
        resource,
        allowParameters,
        denyParameters,
        allowFields,
        denyFields,
      }
    })
    .filter(
      ({ denyFields, denyParameters }) =>
        denyParameters !== '*' || denyFields !== '*'
    )
}

function getGraphQLPermissionsfromToken(permissionsObject) {
  return permissionsObject
    .split(',')
    .map((permission) => {
      const [
        resource,
        allowParameters,
        denyParameters,
        allowFields,
        denyFields,
      ] = permission.split(':')

      return {
        resource,
        allowParameters,
        denyParameters,
        allowFields,
        denyFields,
      }
    })
    .filter(
      ({ denyFields, denyParameters }) =>
        denyParameters !== '*' || denyFields !== '*'
    )
}

function extractInfo(auth) {
  const { sub, name, 'cognito:username': username } = auth.idToken.payload

  const token = localStorage.getItem(
    `CognitoIdentityServiceProvider.${process.env.VUE_APP_COGNITO_CLIENT_ID}.${username}.idToken`
  )
  const permissions = extractPermissions(auth)

  return {
    sub,
    name,
    username,
    permissions,
    token,
  }
}

const state = {
  token: '',
  sub: null,
  name: '',
  username: '',
  email: '',
  permissions: [],
  tmpAuth: null,
}

const mutations = {
  SET_TOKEN: (state, token) => {
    state.token = token
  },
  SET_PERMISSIONS: (state, permissions) => {
    localStorage.setItem('permissions', JSON.stringify(permissions))
    state.permissions = permissions
  },
  SET_TMP_AUTH: (state, auth) => {
    state.tmpAuth = auth
  },
  SET_USER_INFO: (state, { sub, name, email, username }) => {
    state.sub = sub
    state.name = name
    state.username = username

    const { organizationId } = SessionUtils.getCurrentOrganization()

    sessionStorage.setItem(
      'gtm',
      JSON.stringify({
        user_id: sub,
        organizationId: organizationId ? organizationId : undefined,
      })
    )
  },
}

const actions = {
  async login({ commit }, { username, password }) {
    const response = await handleSignIn(username, password)
    if (response.signInStep) {
      return response.signInStep
    }
    const authInfo = extractInfo(response)

    commit('SET_USER_INFO', authInfo)
    commit('SET_PERMISSIONS', authInfo.permissions)
    commit('SET_TOKEN', authInfo.token)
    return authInfo
  },
  async handleConfirmSignIn({ commit }, { password, form }) {
    const response = await handleConfirmSignIn(password, form)
    if (response.signInStep) {
      return response.signInStep
    }

    const authInfo = extractInfo(response)

    commit('SET_TMP_AUTH', null)
    commit('SET_USER_INFO', authInfo)
    commit('SET_PERMISSIONS', authInfo.permissions)
    commit('SET_TOKEN', authInfo.token)
    return authInfo
  },

  async handleResetPassword({ commit }, username) {
    const data = await handleResetPassword(username)

    commit('SET_TMP_AUTH', username)

    return data
  },
  async handleConfirmResetPassword({ commit, state }, { code, password }) {
    const data = await handleConfirmResetPassword(state.tmpAuth, code, password)

    commit('SET_TMP_AUTH', null)

    return data
  },
  async refreshSession() {
    const { signInDetails } = await currentAuthenticatedUser(true)
    try {
      sessionStorage.clear()
      localStorage.clear()
    } catch (e) {
      console.error(e)
    }
    return signInDetails
  },
  async logout() {
    await handleSignOut()

    try {
      sessionStorage.clear()
      localStorage.clear()
    } catch (e) {
      console.error(e)
    }
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
}
